import { useEffect, useState } from 'react'
import {
  findNodeById,
  getParentNode,
  isChildrenLoaded,
  isNodeTypeFolderLike,
  isPlaceholderNode,
  newPlaceholderNode,
  TreeNode,
} from './treeModel'
import { queryNodes } from './navigationApi'

export type TreeState = {
  treeData: TreeNode[]
  selectedNode: TreeNode | null
  expandedNodeIds: string[]
  reconcileCounter: number
  selectNode: (nodeId: string | null) => void
  setExpandedNodeIds: (nodeIds: string[]) => void
  loadChildren: (parentNodeId: string | null) => void
  refreshChildren: (nodeId: string) => void
  needReconsile: () => void
  error: string | null
}

export const useTreeState = (): TreeState => {
  const [treeData, setTreeData] = useState<TreeNode[]>([])
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null)
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([])
  const [reconcileCounter, setReconcileCounter] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)

  const clearError = () => setError(null)

  useEffect(() => {
    loadSites()
  }, [])

  useEffect(() => {
    if (!selectedNode) return
    if (isPlaceholderNode(selectedNode)) return
    if (isChildrenLoaded(selectedNode)) return
    loadChildren(selectedNode.id)
  }, [selectedNode])

  const loadSites = (): void => {
    loadChildren(null)
  }

  const loadChildren = async (parentNodeId: string | null): Promise<void> => {
    clearError()
    await _loadChildren(parentNodeId)
  }

  const _loadChildren = async (parentNodeId: string | null) => {
    if (!parentNodeId) {
      const result = await queryNodes()

      if (!result.success) {
        const error = result.error || "Failed to load sites"
        handleChildrenLoadingFailed(null, error)
        return
      }

      const sites = result.data || []
      setTreeData(sites)
      return
    }

    const node = findNodeById(treeData, parentNodeId)
    if (!node) return

    switch (node.type) {
      case 'site': {
        const siteId = node.id
        const result = await queryNodes({ siteId })

        if (!result.success) {
          const error = result.error || "Failed to load drives"
          handleChildrenLoadingFailed(siteId, error)
          return
        }

        const drives = result.data || []
        updateNodeChildren(treeData, siteId, drives)
        setTreeData([...treeData])
        break
      }
      case 'drive': {
        const siteId = node.parentIds.siteId!
        const driveId = node.id
        const result = await queryNodes({ siteId, driveId })

        if (!result.success) {
          const error = result.error || "Failed to load folders"
          handleChildrenLoadingFailed(driveId, error)
          return
        }

        const nodes = result.data || []
        updateNodeChildren(treeData, driveId, nodes)
        setTreeData([...treeData])
        break
      }
      case 'folder': {
        const siteId = node.parentIds.siteId!
        const driveId = node.parentIds.driveId
        const itemId = node.id
        const result = await queryNodes({ siteId, driveId, itemId })

        if (!result.success) {
          const error = result.error || "Failed to load folders contents"
          handleChildrenLoadingFailed(itemId, error)
          return
        }

        const nodes = result.data || []
        updateNodeChildren(treeData, itemId, nodes)
        setTreeData([...treeData])
        break
      }
      case 'file':
      case 'placeholder':
        break
    }
  }
  
  const updateNodeChildren = (
    nodes: TreeNode[],
    id: string,
    children: TreeNode[]
  ) => {
    const node = findNodeById(nodes, id)
    if (!node) return
    node.children = children
  }

  const selectNode = (nodeId: string | null) => {
    if (!nodeId) return

    const node = findNodeById(treeData, nodeId)
    if (!node || !isNodeTypeFolderLike(node)) return

    setSelectedNode(node)

    const parentNodeIdsToExpand: string[] = []
    let parentNode = getParentNode(treeData, node)

    while (parentNode) {
      const parentNodeId = parentNode.id
      parentNodeIdsToExpand.push(parentNodeId)
      parentNode = getParentNode(treeData, parentNode)
    }

    if (parentNodeIdsToExpand.length) {
      setExpandedNodeIds((currentExpandedNodeIds: string[]) => Array.from(
        new Set([...currentExpandedNodeIds, ...parentNodeIdsToExpand])
      ))
    }
  }

  const hasAncestor = (node: TreeNode, ancestorId: string): boolean => {
    let parentNode = getParentNode(treeData, node)
    while (parentNode) {
      const parentNodeId = parentNode.id
      if (parentNodeId === ancestorId) return true
      parentNode = getParentNode(treeData, parentNode)
    }
    return false
  }

  const stripChildrenNodeIds = (parentNode: TreeNode, nodeIds: string[]): string[] => {
    const result: string[] = []
    const parentNodeId = parentNode.id
    for (const nodeId of nodeIds) {
      const node = findNodeById(treeData, nodeId)
      if (!node) continue
      if (hasAncestor(node, parentNodeId)) continue
      result.push(nodeId)
    }
    return result
  }

  const refreshChildren = (nodeId: string) => {
    const node = findNodeById(treeData, nodeId)
    if (!node) return
    setExpandedNodeIds(stripChildrenNodeIds(node, expandedNodeIds))
    node.children = [newPlaceholderNode()]
    loadChildren(nodeId)
    needReconsile()
  }

  const handleChildrenLoadingFailed = (nodeId: string | null, error: string) => {
    setError(error)
  }

  const needReconsile = () => setReconcileCounter((current) => current + 1)

  return {
    treeData,
    selectedNode,
    expandedNodeIds,
    reconcileCounter,
    selectNode,
    setExpandedNodeIds,
    loadChildren,
    refreshChildren,
    needReconsile,
    error,
  }
}
